import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainPageLayout from '../MainPageLayout';
import ErrorFallbackImage from '../../../assets/images/ErrorFallbackImage.svg';
import PrimaryButton from '../ButtonsV2/PrimaryButton';

/**
 * ErrorFallback Component
 *
 * A specialized fallback UI that appears when an unhandled exception
 * is caught by the ErrorBoundary. It provides a friendly message,
 * an illustrative image, and an action: "Go Home" to redirect users
 * to the root route.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.resetErrorBoundary - A function provided by react-error-boundary
 * to reset the error boundary's state and retry rendering the child components.
 * @returns {JSX.Element} A user-friendly error screen with navigation option.
 */
const ErrorFallback = ({ resetErrorBoundary }) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate('/');
  };

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <div className="flex flex-col flex-1 items-center justify-center px-4 text-center">
        <h2 className="text-custom-light-blue text-2xl md:text-3xl font-semibold mb-4">
          Oops! Something went wrong
        </h2>

        <img
          src={ErrorFallbackImage}
          alt="Error illustration"
          className="w-full max-w-xs h-auto mb-4"
        />

        <p className="text-base md:text-lg text-black mb-6">
          We're working on fixing this issue. Please try again later
        </p>

        <PrimaryButton onClick={handleGoHome}>Go Home</PrimaryButton>
      </div>
    </MainPageLayout>
  );
};

export default ErrorFallback;
